/*
 * @Author: Merlin
 * @Date: 2024-06-11 11:28:12
 * @LastEditors: Merlin
 * @LastEditTime: 2025-02-05 14:03:14
 * @Description: 方法库 🐦‍🔥🐦‍🔥🐦‍🔥 这里存放一些纯函数（pure function） 
 */
import { useRatesStore } from '~/stores/cansino/rates';
import { useUserStore } from '~/stores/user'; 
import {
    oPnlFlat,
    oLongShort,
    oColorInfo, 
    oDefaultCoin,
    WavesContractStateAll,
} from './wavesData';
import {
    chartState_r,
    settingsState_f,
    oPositionData_i,
    fnSettingCenter_Ft,
} from './wavesControlCenter';


// ↓ 返回 "爆仓价格" 为字符形态 
export const fnCalcBust02 = (
    v01:number,
    iTailLong=2,
    nn=false, // 为 true 时将破坏左侧整数部分，慎用  
):string => {
    if (!v01) return "";
    const v01String = String(v01);
    const [sHead, sTail=''] = v01String.split(".");
    const sFinalTail = sTail.slice(0, iTailLong);
    const sResult = (
        sFinalTail
        ? [sHead, sFinalTail].join(".")
        : !sFinalTail && nn ? sHead.slice(0, iTailLong) : sHead 
    );
    return sResult;
}


// ↓ 返回：按“指定比例” 涨跌之后的金额 
// ↓ 即是计算器第2列的数值 
// DH = ({priceMove: e=0, entryPrice: t=0})=>(e / 100 + 1) * t 
export const fnGetAssumedExitPrice_ae = ({ 
    entryPrice=0, // 入场价
    priceMove=0, // 涨幅百分比（有可能是负数）
}):number => {
    const fResult = (
        (priceMove / 100 + 1) // 示例：上涨30(%) / 100 + 1 = 1.3 
        *
        entryPrice
    );
    return fResult;
}

// ↓ 计算 ROI 值（即回报率）返回20，表示 ROI = 20%  
// ↓ ROI = Return on Investment  
// VH as cj as na
export const fnGetRoi_na = ({ 
    wager,
    pnl,
}) => {
    if (!wager) return 0;
    // ↓ 利润50 / 本金100 * 100 = 50(%) 利润率  
    const firstChoice = pnl / wager * 100;
    // ↓ 这是损失的极限，-100%
    const maxToLose = -100;
    const result = (
        firstChoice > maxToLose // 满足合理性即采用 
        ? firstChoice
        : maxToLose
    );
    return result;
};

// ↓ 计算点位的 “涨跌范围的百分数” ❇️ 百分率 x% 
// ↓ 返回 50 表示上涨或下跌了 50%（只返回正数） 
// ↓ 入场 10，退场 11，与退场 9 都将得到结果 10，表示涨跌范围是 10% 
// ↓ 计算结果将做为 priceMove 传递到 fnGetCoeff() 中使用 
export const fnGetPriceMoveRate4Coeff = ({
    entryPrice,
    exitPrice,
})=>{
    // 这里甚至不考虑 long/short 类型 
    if (entryPrice && exitPrice){
        // (退场点位 101 / 入场点位 100 - 1) * 100 = 涨了 1(%) 
        // (退场点位 150 / 入场点位 100 - 1) * 100 = 涨了 50(%)  
        // (退场点位 100 / 入场点位 160 - 1) * 100 = 涨了 -37.5(%)
        // ↑ 补充说明：跌幅 37.5% 即跌至原来的 62.5% * 入场点位 160点 = 100点 
        const v01 = 100 * (exitPrice / entryPrice - 1);
        // ↓ 最后返回一个正数 
        return Math.abs(v01);
    }
    return 0;
}

// =============================================
// =============================================
// ========= ↑ 简单计算 ==========================
// ========= ↓ 复杂的计算 ========================== 
// =============================================
// =============================================


// ↓ 返回 “爆仓点位” 的数值 💡 
// ↓ 用于：在【计算器窗口】计算预演的爆仓值 
// ↓ 用于：用户输入的“止损点数” 的对比值
// ↓ 说明：某股跌到 80点就爆仓了，用户输入止损在 70点不合情理。防止这类错误的无效的输入 
// ↓ 买涨时：点数 < 此值，报错：futures.errors.lossPriceBust = 止损价格不能超过爆仓价格
// ↓ 买跌时：点数 > 此值，报错： 同上  
export const fnGetLiquidationPrice_yO = ({ 
    entryPrice,
    positionType,
    multi, // 杠杆 
    bustBuffer,
    bufferMultiplier,
})=>{
    // ↓ 推断是一个“提前刹车距离” 不同币种有不同的配置值 
    // ↓ 类似在开车 20km/h 时提前 1米踩刹车，在 90km/h 时提前 8米踩刹车（平仓） 
    // ↓ 取得一个 “final bust buffer”，结果值很小，常见规模：0.000x 
    const finalBustBuffer = (
        // ↓ A值 0.000x * B值（通常是 1） 
        // ↓ 这两值的来源都是从配置对象中取得，该对象从后台查询得出，不同币种将取到不同的值 
        bustBuffer * bufferMultiplier 
    );
    // ↓ 计算一个爆仓的比例值，示例如下：
    // ↓ 例：应用 01 倍杠杆时，比例 ≈ 0.0001 即 finalBustBuffer 本身（一个 0.000x 的数字）
    // ↓ 例：应用 05 倍杠杆时，比例 ≈ 0.800 （下跌到入场价的“八成” 就会被平仓） 
    // ↓ 例：应用 10 倍杠杆时，比例 ≈ 0.901 
    // ↓ 例：应用 30 倍杠杆时，比例 ≈ 0.968 
    const ratioBustOn = (
        1
        - 1 / multi // ← 示例：1 ÷ 10倍杠杆 = 0.1 ◆ 至此运算得到 0.9
        + finalBustBuffer // ← 最后追加修正值 ◆ 至此运算得到 0.9001  
    ); 
    const fResult = (
        positionType === oLongShort.long  
        // ↓ 计算买“涨” 爆仓值
        // ↓ 买涨时：在 10 倍杠杆时，点位跌至入场价的 0.9001 倍时，爆仓！ 
        ? entryPrice * ratioBustOn
        // ↓ 计算买“跌” 爆仓值
        // ↓ 买跌时：在 5 倍杠杆时：点位从 100 涨到 120 时爆仓 (1.2倍)  
        // ↓ 以下公式将得出这个数字 120。 详见下方说明： 
        : entryPrice * 2 - entryPrice * ratioBustOn 
    );
    /*  买跌示例： 
        200 * 5倍杠杆 = 1000元
        借股出售：借25股 * 40元/股 = 1000元 
        然后上涨了：40元/股 * 1.2 = 48元/股
        购股还债：购25股 * 48元 = 1200元（强制平仓了） 
        用户的本金 200 购股还债了，归零损失掉了  
        最初售出25股所得的 1000 元也购股还债了 
        ↓ 示例，此公式预先算出了：从40元涨到 48元时强制平仓  
        40 * 2 - 40 * 0.8 = 48元
    */
    return fResult; 
}


// ↓ 推算 wager 最大的合理值  
// ↓ 示例：用户余额 100，但在 flat 模式最大下注值 ≈83 (另有≈17得交手续费）
// sj as cv as Ae() 
export const fnGetMaxWager01_Ae = ({
    multi, // 杠杆倍数  
    balance, 
    flatCoeff, // 例如：0 或 0.0005(万分之五) 
}) => {
    // [
    //     1 + 5 * 0 * 2,      // 1.000 
    //     1 + 5 * 0.0005 * 2, // 1.005 
    //     1 + 30 * 0.0005 * 2, // 1.03 
    //     1 + 90 * 0.0005 * 2, // 1.09 
    //     1 + 200 * 0.0005 * 2, // 1.2 
    //     1 + 600 * 0.0005 * 2, // 1.6 
    // ];
    const v01 = (
        // 当 flatCoeff 为 0 的时候，结果是 1 
        // 那么最终返回 balance 原始值 
        1 + multi * flatCoeff * 2 
    );
    // 示例：
    // 本金 100 / 1.2 = 83.33 
    // 本金 100 / 1.6 = 62.5 
    const result = balance / v01;
    return result;
}

// ↓ 返回：用户输入的对向值 🔰 
// ↓ 监听到输入或点数变动后，计算输入框的对向值 
export const getOppositeValue = ({
    index, // 输入框编号
    triggerPrice, // 输入框数值 
    currentPrice, // 当前点数
    position, // 杠杆后资金总额（实付金额 x 杠杆）
    // ----------------------------------------- 
    feeType, // pnl, flat 
    positionType, // long, short 
    // ---------------------------------------------
    coeff = 1, // 止盈 Pnl 专用，默认为1（原版中见到值：固定计费：0.00035， 固定计费：0.46） 
    bustBuffer, // 止损专用 
})=>{
    // ↓ 这是此方法运行的必要值 
    const allCame = position && triggerPrice && currentPrice && index; 
    if (!allCame) return 0;
    const isModeLong = positionType === oLongShort.long; 
    const isModeShort = positionType === oLongShort.short; 
    const isTypeFlat = feeType === oPnlFlat.flat; 
    const isTypePnl = feeType === oPnlFlat.pnl; 
    const longAndFlat = isModeLong && isTypeFlat; 
    const longAndPnl = isModeLong && isTypePnl; 
    const shortAndFlat = isModeShort && isTypeFlat; 
    const shortAndPnl = isModeShort && isTypePnl; 
    let iTheFinalResult = 0; 
    let iShortResult = 0; 
    switch (index) {
        // ↓ 输入止盈点数，推算止盈金额 
        case 1:
            // ↓ 杠杆后总额 / 当前点数 * 输入框值 - 杠杆后总额 
            const i01_long = position / currentPrice * triggerPrice - position;
            const i01_short = (currentPrice - triggerPrice) * position / currentPrice;
            iShortResult = (
                shortAndFlat
                ? i01_short
                : (shortAndPnl ? i01_short * coeff : 0)
            );
            iTheFinalResult = (
                longAndFlat 
                ? i01_long 
                : (longAndPnl ? i01_long * coeff : iShortResult)
            );
            return iTheFinalResult;
        // ↓ 输入止盈金额，推算止盈点数 
        case 2:
            iShortResult = (
                shortAndFlat
                ? (position - triggerPrice) / (position / currentPrice)
                : (
                    shortAndPnl
                    ? (position - triggerPrice / coeff) / (position / currentPrice)
                    : 0
                )
            );
            iTheFinalResult = (
                longAndFlat
                ? (position + triggerPrice) / (position / currentPrice)
                : (
                    longAndPnl
                    ? (position + triggerPrice / coeff) / (position / currentPrice)
                    : iShortResult
                )
            );
            return iTheFinalResult;
        // ------------ ↑↑↑↑↑↑↑ ------------------------------------  
        // ------------ 盈亏分界 ------------------------------------  
        // ------------ ↓↓↓↓↓↓↓ ------------------------------------  
        // ↓ 输入止损点数 
        case 3:
            iShortResult = (
                shortAndFlat || shortAndPnl
                ? ((1 - bustBuffer) * currentPrice - triggerPrice) * position / ((1 - bustBuffer) * currentPrice)  
                : 0
            );
            iTheFinalResult = (
                longAndFlat || longAndPnl
                ? position / (currentPrice * (1 + bustBuffer)) * triggerPrice - position
                : iShortResult
            );
            return iTheFinalResult;
        // ↓ 输入止损金额  
        case 4:
            iShortResult = (
                shortAndFlat || shortAndPnl
                ? (position + triggerPrice) / position * currentPrice * (1 - bustBuffer)
                : 0
            );
            iTheFinalResult = (
                longAndFlat || longAndPnl
                ? (position - triggerPrice) / position * currentPrice * (1 + bustBuffer)
                : iShortResult
            );
            return iTheFinalResult;
        default:
            return iTheFinalResult;
    }
}

// ↓ 返回：某个合约的 “净利润” ⚓（去除 pnl 费用之后的利润）赔钱时值为负  
// ↓ 买涨模式时：止盈金额 < 此值，报错：profitLow （盈利额不能小于 P&L） 
// ↓ 换言之：***** 
// ↓ 原版：wO = ({entryPrice: e, exitPrice: t, position: o, wager: n, positionType: r, feeType: s, coeff: a})=>{} 
export const getAfterPnlNetProfit = ({
    entryPrice,
    exitPrice,
    position, // 杠杆后的总仓值 
    wager, // 杠杆前的保证金 
    coeff, // 合约的 coeff 浮点数值（主要用在 pnl 类型的合约上） 
    positionType,
    feeType,
})=>{
    if (!entryPrice || !position) {
        return 0;
    }
    // ↓ EPS 值（earnings per share）亦可为负数，表示赔钱 
    const EPS = (
        positionType === oLongShort.long
        ? exitPrice - entryPrice // 正数为赚，负数则赔 
        : entryPrice - exitPrice // 同上 
    );
    // ↓ 购股数量 🔶 例如：总仓值800元 / 80元/股 = 共10股 
    const numberOfStocks = position / entryPrice; 
    // ↓ 总利润 🔶 即以上2项的乘积，值可为负  
    const fTotalProfit = EPS * numberOfStocks; 
    const firstChoice = (
        feeType === oPnlFlat.flat || fTotalProfit < 0
        // ↓ 此值应用于 flat 计费模式，或赔钱模式 
        ? fTotalProfit
        // ↓ 在 pnl 计费模式下，玩家只能拿走“总利润的八九成”，因此需要在此计算玩家可拿走的部分  
        : fTotalProfit * coeff 
    );
    // ↓ 杠杆前的保证金转负数，这是一个损失的上限值 
    // ↓ 例如：假使保证金为 200 元，最多只能损失这 200元  
    const maxToLose = wager * -1; 
    // ↓ 取两者中数值更大的 
    const fResult = (
        firstChoice > maxToLose // firstChoice 的合理性在此验证通过后就采用 
        ? firstChoice
        : maxToLose
    );
    return fResult; 
}

/*
var 总仓值 = 409.60 * 20;
var [入场价, 出场价] = [67_003.43, 67_095.99];
var 购股数量 = 总仓值 / 入场价;
var 单股利润空间 = ({
    '买涨': 出场价 - 入场价,
    '买跌': 入场价 - 出场价,
}['买跌']);
console.log('最终盈利：', 购股数量 * 单股利润空间);
*/


// ↓ 提取 coeff 值 🎯 是一个 0.91 这样的一个比例值，用途有很多 
// ↓ 此值是用户的某合约的利润的“最大拿取比例”  
// ↓ 结果示例：0.89 | 0.91 | 0.92 表示在 pnl 模式下用户可拿取的利润比例；净赚 1元，拿走 0.89 元 
// ↓ 用途 1️⃣ 用于辅助计算用户输入值的对向值 
// ↓ 用途 2️⃣ 用于辅助判别用户输入值的合理性 
// ↓ 例如，此函数的返回值将传入 getAfterPnlNetProfit() 中用于取得真实合约的 pnl 值（去除手续费的净利润） 
// ↓ short 时，杠杆后总仓值 < (止盈金额 / 此方法的结果) 会报错如下：
// futures.errors.takeProfitNegative （止盈价格不能小于零）这里的价格可能说的是点数， 
// 此方法生成了计算变量：coeff02_He 
export const fnGetCoeff = ({ 
    amount, // 合约的 positionAmount 属性有直接传递到此 
    coeffRates, // 费用配置对象 
    // ↓ 注意：此值在 pnl 模式专用 💥 在 flat 固定费用模式下，此值根本没有用到 ⚓⚓⚓ 
    // ↓ 默认为0，应对用户没输入值的情况
    // ↓ 此值通过 fnGetPriceMoveRate4Coeff() 取得，表示涨幅百分数，如50，表示涨跌了 50% 
    priceMove=0, // 表示涨跌幅度（百分数，正数）
    // ↓ 为了币种转换新增项 -------------------------- 
    currency='', // 建仓的付款币种 
    recursion=false, // 收到此值表示正在递归调用，这时收到的 amount 表示涨跌幅度 
}) => {
    if (!amount || !coeffRates) 
        return 0;
    const toPrint = (
        1 && !currency && !recursion
        && new Date().getSeconds() % 3 === 0
        && import.meta.dev 
    ); 
    currency ||= (()=>{
        const oNowCurrency = useUserStore().oNowCurrency;
        return oNowCurrency.currencyName;
    })();
    const fUsdOrAmplitude = (()=>{
        if (currency == 'usdt' || recursion) {
            return amount;
        }
        const oRates = useRatesStore();
        const sExchange = `${currency}usdt`;
        const fRate = oRates.comparisonCurrencyList[sExchange];
        const fResult = amount * fRate;
        if (toPrint){
            console.log(`${amount} x ${sExchange}汇率 ${fRate} =`, Number(fResult.toFixed(2))); 
        }
        return fResult;
    })(); 
    // ● 参数 coeffRates 形态例如如下：  
    // ● 在 pnl 模式： { "1": { "0": 0.1, "1": 0.89, ...}, "1000": {}...} 
    // ● 在 flat 模式： { "1": 0.00035, "33334": 0.00036, "100000": 0.00037, ...} 
    const aAmountTierList = Object.keys(coeffRates).map(sKey => {
        return Number(sKey); // 字符转数值 
    }).sort((aa, bb) => {
        return aa - bb; // 升序排列，非常重要！！！ 💥💥💥 
    });
    const aCandidate = aAmountTierList.filter((iThisTier, idx)=>{
        // ↓ 第1条需要保留，无论金额多小，第一条需要做为兜底项 
        if (idx === 0) return true;
        return iThisTier <= fUsdOrAmplitude;
    });
    const iTargetTier = aCandidate.at(-1);
    if (toPrint){
        console.log(`候选档：${~~recursion}`, deepCopy(aCandidate), '\n■■■■■■■■■');
    }
    // ↓ 关于 vConfig 的说明 
    // ↓ flatFee 模式下，得到一个 int 值，示例：1, 33334 等 
    // ↓ pnlFee 模式下，先得到一个对象，示例： { "0": 0.1, "1": 0.898, "5": 0.899, "10": 0.8998} 
    const vConfig = coeffRates[iTargetTier]; 
    const isTypeObject = "object" == typeof vConfig; // pnl 模式专用 
    const isTypeNumber = "number" == typeof vConfig; // flat 模式，或 pnl 的二次递归时调用 
    let fResult = 0; 
    if (isTypeNumber){
        // 计费模式为 flatFee 时，以及 pnlFee 的递归调用时会执行至此  
        fResult = vConfig; // flat 模式杠杆后的总仓值计费  
    } else if (isTypeObject && priceMove){
        // 只有 pnlFee 模式时会执行至此 
        fResult = fnGetCoeff({
            coeffRates: vConfig,
            // ↓ 没错，pnl 是按涨跌幅度“百分数” 推导费率，而且下一行是 priceMove 值首次参与运算 
            amount: priceMove, 
            recursion: true,
        });
    }
    // ↓ 返回值说明：
    // ● pnl 模式下可能得到一个 0.89 那表示玩家可取得 89% 的利润 
    // ● flat 模式下可能得到一个 0.00035 那表示玩家需要为支付合约杠杆后总仓职的 0.00035 * 2 做为手续费 
    return fResult;
}


// ↓ 预估用户合约的 ROI 值， 
export const fnCalcROI_wL = ({
    wager, 
    pnl, 
}) => {
    if (!wager)
        return 0;
    const firstChoice = pnl / wager * 100; 
    const fResult = (
        firstChoice > -100
        ? firstChoice
        : -100
    );
    return fResult; 
}


// ↓延时方法“包” 生成器 
export const fnDelayPackageMaker = (
    fn,
    iDelayMs=0, // 延时 ms 
) => { 
    let theTimer = null;
    let bMark = false;
    let aCallingPool = [];
    return {
        caller: (...arg) => {
            if (bMark) return;
            // ↓ 设定定时器，辞旧迎新， 
            clearTimeout(theTimer);
            theTimer = setTimeout(() => {
                fn(...arg);
            }, iDelayMs);
            // ↓ 每次呼叫都向 aCallingPool 推入定时器，以便于停止定时器时，清除定时器 
            aCallingPool.push(theTimer);
        },
        // ↓ 停止定时器
        stopper: () => { 
            bMark = true; // 为 true 时将阻断 caller 执行 
            theTimer && clearTimeout(theTimer);
            theTimer = null;
            aCallingPool.forEach(oneTimer => {
                clearTimeout(oneTimer);
                oneTimer = null;
            });
            aCallingPool = []; 
        },
        reStarter: () => {
            bMark = false;
        },
    };
}

// ↓延时方法生成器，用于节流 
export const fnDelayFnMaker = (
    fn,
    iDelayMs=0, // 延时 ms 
) => {
    // ↓ 闭包变量 
    let theTimer;
    // ↓ 最终返回函数 
    const fnResult = function(
        arg01, // ← 此处 1 参不解 
        ...argRest
    ) {
        clearTimeout(theTimer);
        if (arg01?.abortDebounce) return;
        theTimer = setTimeout(() => {
            fn.apply(this, argRest);
        }, iDelayMs);
    }
    return fnResult;
}

// ↓ 得出止盈止损的操作模式 
export const getContolType = (iInputNum) => { 
    switch (iInputNum) {
        case 1:
        case 3:
            return "price";
        case 2:
        case 4:
            return "value";
        default:
            return ""; 
    }
}



// ↓ 数值转字符，按币种价格制定小数位数
export const fnGetDisplayValue = (fPrice=0)=>{
    const iTailLong = (()=>{
        if (fPrice < 0.1) return 6;
        else if (fPrice < 1) return 5;
        else if (fPrice < 300) return 4;
        return 2;
    })();
    const sResult = number2LocaleString(
        fPrice,
        iTailLong,
    );
    return sResult;
};

// ↓ 倒计时 
export function useCountdown() {
    // 使用 ref 创建响应式变量
    const intervalRef = ref({ // 存储 setInterval 的 id
        id: null,
    });
    const countDownTime = ref("");  // 用来存储倒计时的字符串形式
    const countDownObj = ref({});   // 存储倒计时的各个时间部分（天、小时、分钟、秒）
    const isActive = ref(false);    // 表示倒计时是否处于激活状态

    // private 
    // 倒计时归零 
    function resetCountdown() {
        countDownTime.value = "00:00:00";
        countDownObj.value = {
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
            formattedDays: "00"
        };
    }

    // private 
    // ↓ 高频调用， 更新倒计时
    function updateCountdown(endTime) {
        const targetTime = new Date(endTime)?.getTime();
        const timeGap = targetTime - Date.Now();
        // ↓ 如果时间已经过去，停止倒计时并重置 
        if (timeGap <= 0 || !timeGap) {
            isActive.value = false;
            stopper(); // 关停
            resetCountdown();
            return;
        }

        isActive.value = true;
        const oneMin = 1000 * 60;
        const oneHr = oneMin * 60;
        const days = Math.floor(timeGap / (oneHr * 24)); 
        const formattedDays = String(days).padStart(2, "0"); 
        const hours = ("0" + Math.floor((timeGap % (oneHr * 24)) / oneHr)).slice(-2);
        const minutes = ("0" + Math.floor((timeGap % oneHr) / oneMin)).slice(-2);
        const seconds = ("0" + Math.floor((timeGap % oneMin) / 1000)).slice(-2);
        // const sDay = days ? days + " " + t("common.Days_", days) : "";
        countDownTime.value = `${hours}:${minutes}:${seconds}`;
        // console.log("倒计时：", countDownTime.value, '后将到达：', new Date(endTime).toLocaleString()); 

        // 更新倒计时对象
        countDownObj.value = {
            days,
            hours,
            minutes,
            seconds,
            formattedDays,
        };
    }

    const starter = (iEndTime)=>{
        stopper();
        if (1 && import.meta.dev){
            console.log("启动或重启倒计时：", new Date(iEndTime).toLocaleString()); 
        }
        updateCountdown(iEndTime);
        intervalRef.value.id = setInterval(() => {
            updateCountdown(iEndTime);
        }, 1_000);
    }
    const stopper = ()=>{
        if (0 && import.meta.dev){
            console.log("关停倒计时", intervalRef.value.id);
        }
        clearInterval(intervalRef.value.id);
    };
    
    return {
        countDownTime,
        countDownTimeObj: countDownObj,
        countDownIsActive: isActive,
        starter,
        stopper,
    };
}

// ↓ 跳至特定币种页
export const jump2TargetCoin = (
    sCoinName='',
    oConfig={},
)=>{
    // ↓ 无条件重定向（同原版，这里必须要指明币种） 
    sCoinName ||= oDefaultCoin.name;
    sCoinName = sCoinName.toUpperCase();
    const path = `/casino/futuresGame/${sCoinName}`; 
    useRouter().push({
        path: getLocalPath(path),
        replace: true, // 不生成历史记录 
        ...oConfig,
    });
};

// ↓ 跳至价格来源说明页 
export function jump2Formulation(){ 
    const sPath = getLocalPath('/casino/futuresGame/priceFormulation');
    window.open(sPath, '_blank');
}


// ↓ 计算 active 合约 pnl 值的方法 
// ↓ 此函数与 fnCalcROI_wL() 类似，预估了合约的 pnl, roi 值 
export function calcActiveContractPnl (oHis){ 
    const pair = oHis.pair;
    const oNewInfo = chartState_r.currentPairsPrices?.[pair];
    const oPairsSettings = settingsState_f.pairsSettings?.[pair]; 
    const fNewPrice = oNewInfo?.value || 0;
    const priceMove = fnGetPriceMoveRate4Coeff({
        entryPrice: oHis.entryPrice,
        exitPrice: fNewPrice, 
    });
    const fCoeff = fnGetCoeff({
        coeffRates: oPairsSettings?.[`${oHis.feeType}Fee`],
        amount: oHis.positionAmount || 0,
        priceMove,
        currency: oHis.currency,
    });
    const oParams = {
        entryPrice: oHis.entryPrice,
        exitPrice: fNewPrice,
        position: oHis.positionAmount || 0,
        wager: oHis.wager || 0,
        positionType: oHis.type,
        feeType: oHis.feeType,
        coeff: fCoeff,
    };
    const fPnl = getAfterPnlNetProfit(oParams);
    // console.log(`预估合约 Pnl 值=`, fPnl);
    return fPnl;
};

// ↓ 取得 active 合约的动态信息 
export function getActiveContractInfo(oHis){
    const oNewState = oPositionData_i.oStates[oHis._id] || {}; 
    const stateRealtime = oNewState.state || ''; 
    // ↓ 广义上的平仓标记 
    const isClosed = (
        !!stateRealtime
        && stateRealtime != WavesContractStateAll.active 
    );
    const oResult = {
        pnl: oNewState.pnl || null,
        roi: oNewState.roi || null,
        stateRealtime,
        isClosed,
    };
    if (!isClosed){
        oResult.pnl = calcActiveContractPnl(oHis);
        oResult.roi = fnCalcROI_wL({ 
            wager: oHis.wager, 
            pnl: oResult.pnl, 
        });
    }
    return oResult;
}


// ↓ 用于校验 pnl 正确性 
// ↓ 开发阶段使用 
export function fnPnlChecker(his){
    // 入参 his 是 history 的意思 
    const coeff = (()=>{
        const oPairsSettings = settingsState_f.pairsSettings?.[
            his.pair
        ]; 
        const priceMove = fnGetPriceMoveRate4Coeff({
            entryPrice: his.entryPrice,
            exitPrice: his.exitPrice,
        });
        const fResult = fnGetCoeff({
            coeffRates: oPairsSettings?.[`${his.feeType}Fee`],
            amount: his.positionAmount || 0,
            priceMove,
            currency: his.currency,
        });
        return fResult; 
    })();
    const myPnl = getAfterPnlNetProfit({
        entryPrice: his.entryPrice,
        exitPrice: his.exitPrice,
        position: his.positionAmount || 0,
        wager: his.wager || 0,
        positionType: his.type,
        feeType: his.feeType,
        coeff,
    });
    const fGap = Math.abs(his.pnl - myPnl);
    const fGapRate = fnGetPriceMoveRate4Coeff({
        entryPrice: his.pnl,
        exitPrice: myPnl,
    });
    // if (fGap > 0.1){
    //     console.log(`pnl 值有差异：${his.wager} x ${his.multi} | 接口值与核算值：`, his.pnl, myPnl);
    // }
    return {fGap, fGapRate, myPnl}; 
}

// ↓ 补充表格行信息 
export function getInfo4Rows(oHis){
    const oSettingCenter = fnSettingCenter_Ft(); 
    const oResult = {
        precision: oSettingCenter.getDecimalsByCurrency(oHis.pair),
        iDecimalPlaces: oSettingCenter.fnGetTheiDecimalPlaces(oHis.pair, oHis.currency), 
        oTypeColor: oColorInfo[oHis.type], // 买涨买跌的合约类型标记 
        stateRealtime: oPositionData_i.oStates[oHis._id]?.state || '', 
        oPNLColor: (() => {
            if (oHis.pnl >= 0) { 
                return oColorInfo.green;
            }else if (oHis.pnl < 0){
                return oColorInfo.red;
            }
            return {}; // 默认颜色 
        })(),
        oROIColor: (() => {
            if ((oHis.roi || 0) >= 0) { // 原版的 0% 显示为绿色 
                return oColorInfo.green;
            }
            return oColorInfo.red;
        })(),
    };
    return oResult; 
}

// =====================================================================================  
// ==== ↓ 用于调试与参考 📕 没有实际作用 =================================================================================  
// =====================================================================================  


// 功能： 添加余额，得加钱！  
// 位置： profile.token
var test01 = () => {
    var t = {user: {currencies: {}}};
    (1 && (()=>{
        const keys = [...Object.keys(t.user.currencies), 'usdt', 'usdc'];
        keys.forEach((key, idx)=>{
            t.user.currencies[key] ||= {};
            t.user.currencies[key].balance = 9000 + idx + Math.random();
        });
        t.user.currencies['usdc'].balance = 0.001;
    })())
    ,0
}


// 功能： 修改原版配置参数 
// 位置： futures.settings 
var test01 = () => {
    var e = {} as any; 
    (
        1 && (()=>{
            const oPairs = structuredClone(e?.data?.pairs);
            if (!oPairs) return; 
            const aEntries = Object.entries(oPairs);
            // aEntries.forEach(([sPair, oPair])=>{
            //     const xx = 1_000;
            //     oPair.maxMultiplier *= xx;
            //     oPair.maxUserPositionAmount *= xx;
            //     oPair.maxWagerAmount *= xx;
            // });
        })()
    ),
    0,
    // ================================= 
    (
        1 && (()=>{
            const sTarget = 'ADA_USDT';
            const oTarget = e?.data?.pairs?.[sTarget];
            if (!oTarget) return; 
            const oNew = {
                ...{
                    "minWagerAmount": 0.03, // 最小下注值（本金） 
                    "maxWagerAmount": 33333,
                },
                ...{
                    "minUserPositionAmount": 6, // 最小下注值（杠杆后） 
                    "maxUserPositionAmount": 666666,
                },
                ...{
                    "bufferMultiplier": 7, // 杠杆率下限 
                    "maxMultiplier": 777, // 杠杆率上限 
                },
            };
            Object.assign(oTarget, oNew); 
            console.log(`修改后 ${sTarget} ↓\n`, structuredClone(oTarget));
        })()
    ),
    0
};

// 功能： 查询大表格数据 
// 位置： futures.positions 
// 4230["futures.positions",{"type":"active","perPage":10,"page":1,"pair":""}]	
// 💥💥💥 显示【全部平仓按钮】 (e.count ||= 18), 0 
var test03 = () => {
    var r = '' as any;
    // --------------------------
    (1 && (()=>{
        if ('active,closed'.includes(r)){
            r='public'
        }
    })())
    ,0
    // ↓ 打印大表格行
    var r = {data: {item: []}} as any; 
    console.log(
        '表格↓：\n', structuredClone(r?.data?.items || [])
    ),
    0,
    console.log(
        '打印表格↓：\n', structuredClone(r?.data?.items || [])
    ),
    (r?.data?.items || []).forEach(cur => {
        cur.name = cur.state
    }),
    0
}

// 功能： 查看动态数据 
// 位置： tick  
var test04 = () => {
    // console.log('t.data.tick', t.data.tick.value), 0 
    var t = {} as any; 
    (
        1 && (()=>{
            const {time, value} = t.data.tick
            var isSame = value === window['last_tick_value'];
            console.log(`时间：${new Date(time*1000).toLocaleString()} 🔶 ${value}, ${isSame ? '💥' : ''}`);
            
            window['last_tick_value'] = value; 
        })()
    ),
    0
}


